/**
 * @fileoverview Servicio de Configuración de Formularios de Eventos
 *
 * Este módulo proporciona servicios administrativos para la gestión de
 * formularios de registro de eventos, incluyendo consulta y actualización
 * de configuraciones. Requiere autenticación mediante fetchWrapper.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import fetchWrapper from "fetchWrapper";

/**
 * Obtiene el formulario de registro de un data source específico.
 *
 * Este método consulta el formulario de registro asociado a una fuente
 * de datos específica, incluyendo su configuración, campos personalizados
 * y estado actual.
 *
 * @param {string} dataSourceId - ID único de la fuente de datos.
 * @throws {Error} Si ocurre un error en la comunicación con el servidor.
 * @returns {Promise<Object>} Objeto con status y data del formulario.
 */
export const getEventFormByDataSource = async (dataSourceId) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/event_registration_forms/data_source/${dataSourceId}`
    );

    if (response.status === 429) {
      return {
        status: false,
        message: "Has excedido el límite de solicitudes. Por favor, intenta de nuevo más tarde.",
      };
    }

    const result = await response.json();
    console.log("Respuesta cruda del backend (getEventFormByDataSource):", result);

    // El backend puede devolver success o status
    const isSuccess = result.success === true || result.status === true;

    if (isSuccess) {
      // El backend puede devolver payload o data
      const data = result.payload || result.data;
      console.log("Datos extraídos:", data);
      return { status: true, data: data };
    } else {
      const message = result.message || "Error al obtener el formulario";
      console.error("Error del backend:", message);
      return { status: false, message };
    }
  } catch (error) {
    console.error("Error en getEventFormByDataSource:", error);
    const message = error.message || "Error al obtener el formulario";
    return { status: false, message };
  }
};

/**
 * Actualiza la configuración de un formulario de registro.
 *
 * Este método permite modificar los parámetros de configuración de un
 * formulario de registro, incluyendo fechas de vigencia, límites de
 * registros, estado activo/inactivo y campos personalizados.
 *
 * @param {string} dataSourceId - ID único de la fuente de datos.
 * @param {Object} formData - Datos de configuración a actualizar.
 * @param {string} [formData.form_start_date] - Fecha de inicio del formulario.
 * @param {string} [formData.form_end_date] - Fecha de finalización del formulario.
 * @param {string} [formData.card_effective_date] - Fecha de vigencia del carnet.
 * @param {string} [formData.card_expiration_date] - Fecha de expiración del carnet.
 * @param {boolean} [formData.is_active] - Estado activo/inactivo del formulario.
 * @param {number} [formData.max_registrations] - Cantidad máxima de registros permitidos.
 * @param {Array} [formData.custom_fields] - Campos personalizados del formulario.
 * @throws {Error} Si ocurre un error en la comunicación con el servidor.
 * @returns {Promise<Object>} Objeto con status y data del formulario actualizado.
 */
export const updateEventRegistrationForm = async (dataSourceId, formData) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/event_registration_forms/edit/data_source/${dataSourceId}`,
      JSON.stringify(formData),
      "PUT"
    );

    if (response.status === 429) {
      return {
        status: false,
        message: "Has excedido el límite de solicitudes. Por favor, intenta de nuevo más tarde.",
      };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      return { status: true, data: result.payload };
    } else {
      const message = result.message || "Error al actualizar el formulario";
      return { status: false, message };
    }
  } catch (error) {
    const message = error.message || "Error al actualizar el formulario";
    return { status: false, message };
  }
};
